import { computed, inject, Injectable } from '@angular/core';
import { Provider } from '@common/enums';
import {
  hasAdminRole,
  hasDomainAdminRole,
  hasDomainUserRole,
  hasEmergencySignInRole,
  hasLimitedAdminRole,
  hasProviderRole,
  hasSingleUserRole,
  hasUserAccountAdminRole,
  ProductMode
} from '@common/models';
import { AuthStateService } from '@common/services/auth/auth-state.service';
import { isHomeUser, isProviderGoogle, isProviderOffice } from '@common/utils';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class AbilityService {
  private readonly authState = inject(AuthStateService);

  readonly #authUser = this.authState.authUser;
  readonly #defaultObjectLockCount = this.authState.defaultObjectLockCount;
  readonly #roles = this.authState.roles;
  readonly #systemInfo = this.authState.systemInfo;

  public readonly isEmergencySignIn = computed(() => hasEmergencySignInRole(this.#roles()));
  public readonly isProvider = computed(() => hasProviderRole(this.#roles()));

  public readonly isAdmin = computed(() => hasAdminRole(this.#roles()));
  public readonly isDomainAdmin = computed(() => hasDomainAdminRole(this.#roles()));
  public readonly isLimitedAdmin = computed(() => hasLimitedAdminRole(this.#roles(), this.authState.providerRoles()));
  public readonly isUserAccountAdmin = computed(() => hasUserAccountAdminRole(this.#roles()));

  public readonly isDomainUser = computed(() => hasDomainUserRole(this.#roles()));
  public readonly isSingleUser = computed(() => hasSingleUserRole(this.#roles()));

  public readonly isRestoreEnable = computed(() => this.#authUser().RestoreEnable);
  public readonly isSignInEnable = computed(() => this.#authUser().SignInEnable);

  public readonly isBusinessOffice = computed(() => this.#authUser().Provider == Provider.OfficeBusiness);
  public readonly isHomeUser = computed(() => isHomeUser(this.#authUser()));
  public readonly isGoogle = computed<boolean>(() => isProviderGoogle(this.authState.authUser()?.Provider));
  public readonly isOffice = computed<boolean>(() => isProviderOffice(this.authState.authUser()?.Provider));

  public readonly isAlternateSignIn = computed<boolean>(() => {
    const isEmergencySignIn = this.isEmergencySignIn();

    return environment.production ? isEmergencySignIn : isEmergencySignIn || (this.isProvider() && this.isUserAccountAdmin());
  });

  public readonly isGAA = computed<boolean>(() => this.#systemInfo()?.GAA);
  public readonly isImmutability = computed<boolean>(() => this.#systemInfo()?.IsImmutability || (this.#defaultObjectLockCount() ?? 0) > 0);
  public readonly isStandalone = computed<boolean>(() => this.#systemInfo()?.ProductMode === ProductMode.Resale);
}
